import { animate } from 'animejs';
import utils from '../utils/utils';

// ── DEV: set to 0 to disable ─────────────────────────────────────────────────
const SIMULATE_LOAD_MS = 0;
// ─────────────────────────────────────────────────────────────────────────────

class Loader {
  constructor(onReady) {
    let cache       = {};
    let pathLength  = 0;
    let startTime   = Date.now();
    let isContentLoaded = false;
    let loadedCount = 0;
    let totalAssets = 0;
    let countCurrent = 0;
    let rafId        = null;

    // ── Cache DOM ────────────────────────────────────────────────────────────
    let initCache = () => {
      cache.loader    = document.querySelector('.loader');
      cache.fill      = document.querySelector('.lotus_icon .lotus-path-1-fill');
      cache.ghost     = document.querySelector('.lotus_icon .lotus-path-1-ghost');
      cache.trace     = document.querySelector('.lotus_icon .lotus-path-1-trace');
      cache.path2     = document.querySelector('.lotus_icon .lotus-path-2');
      cache.path3     = document.querySelector('.lotus_icon .lotus-path-3');
      cache.countdown = document.querySelector('.loader .countdown');
    };

    // ── Countdown (RAF-driven, time-based) ───────────────────────────────────
    let formatCount = (n) => String(Math.floor(n)).padStart(3, '0');

    let tickCountdown = () => {
      const elapsed = Date.now() - startTime;

      // Always time-based — never reaches 100 here.
      // exitLoader() is the only place that sets 100 and cancels this RAF.
      const countTarget = elapsed < 3000
        ? (elapsed / 3000) * 95                           // 0 → 95 over 3 s
        : Math.min(99, 95 + (elapsed - 3000) * 0.0008);  // slow crawl past 3 s

      if (countCurrent < countTarget) {
        const gap   = countTarget - countCurrent;
        const speed = Math.max(0.3, gap * 0.06);
        countCurrent = Math.min(countCurrent + speed, countTarget);
        if (cache.countdown) cache.countdown.textContent = formatCount(countCurrent);
      }

      rafId = requestAnimationFrame(tickCountdown);
    };

    // ── Asset tracking ────────────────────────────────────────────────────────
    let trackAssets = () => {
      const modules = Array.from(
        document.querySelectorAll('.wrapper [class*="module-"]')
      ).slice(0, 3);

      const assets = [];
      modules.forEach(mod => {
        mod.querySelectorAll('img, video').forEach(el => assets.push(el));
      });

      totalAssets = assets.length;

      let markLoaded = () => {
        isContentLoaded = true;
      };

      if (SIMULATE_LOAD_MS > 0) {
        setTimeout(markLoaded, SIMULATE_LOAD_MS);
      }

      if (totalAssets === 0 && SIMULATE_LOAD_MS === 0) {
        markLoaded();
        return;
      }

      let onAssetLoad = () => {
        loadedCount++;
        if (loadedCount >= totalAssets && SIMULATE_LOAD_MS === 0) {
          markLoaded();
        }
      };

      assets.forEach(el => {
        if (el.tagName === 'IMG') {
          if (el.complete && el.naturalWidth > 0) {
            onAssetLoad();
          } else {
            el.addEventListener('load',  onAssetLoad, { once: true });
            el.addEventListener('error', onAssetLoad, { once: true });
          }
        } else {
          // Skip videos that won't buffer on their own (preload="none" / no src)
          if (!el.src && !el.querySelector('source[src]')) {
            totalAssets--;
            if (loadedCount >= totalAssets && SIMULATE_LOAD_MS === 0) markLoaded();
            return;
          }
          if (el.getAttribute('preload') === 'none') {
            totalAssets--;
            if (loadedCount >= totalAssets && SIMULATE_LOAD_MS === 0) markLoaded();
            return;
          }
          if (el.readyState >= 3) {
            onAssetLoad();
          } else {
            el.addEventListener('canplay', onAssetLoad, { once: true });
            el.addEventListener('error',   onAssetLoad, { once: true });
          }
        }
      });

      // Hard-exit safety net: never wait more than 10 s for assets
      if (SIMULATE_LOAD_MS === 0) setTimeout(markLoaded, 10000);
    };

    // ── Reset lotus to pre-animation state (for replay) ──────────────────────
    let resetLotus = () => {
      cache.trace.style.strokeDashoffset = pathLength;
      cache.trace.style.opacity = '1';
      cache.ghost.style.opacity = '0.3';
      cache.fill.style.opacity = '0';
      cache.path2.style.opacity = '0';
      cache.path2.style.transform = 'scale(0.75)';
      cache.path3.style.opacity = '0';
      cache.path3.style.transform = 'scale(0.75)';
    };

    // ── Play one forward cycle ────────────────────────────────────────────────
    let playForward = (onCycleComplete) => {
      animate(cache.trace, {
        strokeDashoffset: [pathLength, 0],
        duration: 1500,
        ease: 'inOutCubic',
        onComplete: () => {
          animate(cache.ghost, { opacity: [0.3, 0], duration: 250, ease: 'linear' });
          animate(cache.trace, { opacity: [1, 0],   duration: 250, ease: 'linear' });
          animate(cache.fill,  { opacity: [0, 1],   duration: 250, ease: 'linear' });
        }
      });

      animate(cache.path2, {
        opacity:  [0, 1],
        scale:    [0.75, 1],
        duration: 700,
        delay:    1200,
        ease:     'outCubic',
      });

      animate(cache.path3, {
        opacity:  [0, 1],
        scale:    [0.75, 1],
        duration: 800,
        delay:    1700,
        ease:     'outCubic',
        onComplete: onCycleComplete,
      });
    };

    // ── Revert animation ─────────────────────────────────────────────────────
    let playRevert = (onComplete) => {
      animate(cache.path3, {
        opacity:  [1, 0],
        scale:    [1, 0.75],
        duration: 400,
        ease:     'inCubic',
      });

      animate(cache.path2, {
        opacity:  [1, 0],
        scale:    [1, 0.75],
        duration: 400,
        delay:    250,
        ease:     'inCubic',
      });

      animate(cache.fill, {
        opacity:  [1, 0],
        duration: 350,
        delay:    450,
        ease:     'linear',
      });

      animate(cache.trace, {
        opacity:  [1, 0],
        duration: 350,
        delay:    450,
        ease:     'linear',
        onComplete: () => {
          setTimeout(() => {
            resetLotus();
            onComplete();
          }, 80);
        }
      });

      animate(cache.ghost, {
        opacity: [0, 0],
        duration: 1,
        delay: 450,
        ease: 'linear',
      });
    };

    // ── Exit loader ───────────────────────────────────────────────────────────
    let exitLoader = () => {
      const elapsed  = Date.now() - startTime;
      const minDelay = Math.max(0, 3000 - elapsed);

      setTimeout(() => {
        cancelAnimationFrame(rafId);
        countCurrent = 100;
        if (cache.countdown) cache.countdown.textContent = '100';

        const finish = () => {
          utils.enableScroll();
          cache.loader.remove();
          if (typeof onReady === 'function') onReady();

          // Slide the nav in (mirrors the post-navigation behaviour).
          // Smooth-scroll to any anchor only after the nav has finished sliding in,
          // then fix up the nav state once the scroll settles.
          const hash = window.location.hash;
          const anchorTarget = hash ? document.querySelector(hash) : null;

          window.__nav?.slideHeaderIn(0, () => {
            if (anchorTarget) {
              anchorTarget.scrollIntoView({ behavior: 'smooth' });
              window.addEventListener('scrollend', () => {
                document.querySelector('header')?.classList.remove('up');
                window.__nav?.syncPosition();
              }, { once: true });
            }
          });
        };

        const wrapper = document.querySelector('.wrapper');
        if (wrapper) {
          // Ensure scroll is at top before the page becomes visible
          window.scrollTo(0, 0);
          // Hide the nav before the wrapper slides up so it can slide in after
          window.__nav?.resetInstant();
          // Slide the page up from below to cover the static loader
          wrapper.style.zIndex = '99999999';
          animate(wrapper, {
            y: ['100vh', '0px'],
            duration: 900,
            ease: 'inOutCubic',
            onComplete: () => {
              wrapper.style.zIndex = '';
              wrapper.style.transform = '';
              finish();
            }
          });
        } else {
          finish();
        }
      }, minDelay);
    };

    // ── State machine ─────────────────────────────────────────────────────────
    let afterCycle = () => {
      if (isContentLoaded) {
        exitLoader();
      } else {
        playRevert(() => playForward(afterCycle));
      }
    };

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    let initFunc = () => {
      utils.disableScroll();
      initCache();

      // If any required element is missing, remove loader and bail cleanly
      if (!cache.loader || !cache.fill || !cache.ghost || !cache.trace || !cache.path2 || !cache.path3) {
        if (cache.loader) cache.loader.remove();
        utils.enableScroll();
        if (typeof onReady === 'function') onReady();
        return;
      }

      pathLength = cache.trace.getTotalLength();
      cache.trace.style.strokeDasharray  = pathLength;
      cache.trace.style.strokeDashoffset = pathLength;
      cache.trace.style.visibility = 'visible';

      window.scrollTo(0, 0);

      rafId = requestAnimationFrame(tickCountdown);
      trackAssets();
      playForward(afterCycle);
    };

    initFunc();
  }
}

export { Loader }
